<template>
  <MasterLayout>
    <section>
      <Navbar />
    </section>
  <div class="manage-beneficiaries">
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-spinner">
      <Loader />
    </div>

    <div v-else>
      <!-- Header with Back Button -->
      <div class="page-header">
        <button @click="goBack" class="back-btn">
          <span class="back-icon">←</span>
          Back
        </button>
      </div>

      <!-- Program Details Section -->
      <div class="program-details">
        <h1>Manage Beneficiaries: {{ couponData.title || couponData.couponName || couponData.programName || 'Unknown Program' }}</h1>
        <div class="program-meta">

          <span v-if="type == 'coupons'" class="meta-item">
            <strong>Code:</strong> {{ couponData.couponCode || 'N/A' }}
          </span>
          <span class="meta-item">
            <strong>Status:</strong>
            <span :class="['status-badge', getStatusClass()]">
              {{ getStatusText() }}
            </span>
          </span>
          <span class="meta-item">
            <strong>Period:</strong> {{ formatPeriod() }}
          </span>
          <span class="meta-item">
            <strong>Type:</strong> 
              {{ type === 'coupons' 
                  ? (couponData.couponType === 1 ? 'Percentage' : 'Fixed Amount') 
                  : couponData.rewardType || 'N/A' }}
          </span>
          <span class="meta-item">
            <strong>Value:</strong> {{ formatValue() }}
          </span>
        </div>
      </div>

      <!-- Stats Cards -->
      <div class="stats-container">
        <div class="stat-card">
          <div class="stat-header">
            <h3>Total Redemptions</h3>
          </div>
          <p class="stat-number">{{ formatNumber(couponData.usedCount || 0) }}</p>
        </div>
        
        <div class="stat-card">
          <div class="stat-header">
            <h3>Pending Redemptions</h3>
          </div>
          <p class="stat-number">{{ formatNumber(pendingRedemptions) }}</p>
        </div>
        
        <div class="stat-card">
          <div class="stat-header">
            <h3>Total Beneficiaries</h3>
          </div>
          <p class="stat-number">{{ formatNumber(totalBeneficiaries) }}</p>
        </div>
        
        <div class="stat-card">
          <div class="stat-header">
            <h3>Remaining Uses</h3>
          </div>
          <p class="stat-number">{{ formatNumber(remainingUses) }}</p>
        </div>
      </div>

      <!-- Tab Controls -->
      <div class="tab-container">
        <div class="tab-header">
          <button 
            v-for="tab in tabs" 
            :key="tab.key"
            @click="activeTab = tab.key"
            :class="['tab-btn', { active: activeTab === tab.key }]"
          >
            {{ tab.label }}
            <span class="tab-count">{{ getTabCount(tab.key) }}</span>
          </button>
        </div>
      </div>

      <!-- Controls Section -->
      <div class="controls-section">
        <div class="search-filters">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search by name or email..."
            class="search-input"
          />
          <select v-model="usageFilter" class="filter-select">
            <option value="">All Status</option>
            <option value="used">Used</option>
            <option value="unused">Unused</option>
          </select>
        </div>

        <!-- Actions -->
        <div class="actions-section">
          <button 
            v-if="activeTab === 'available' && selectedBeneficiaries.length > 0"
            @click="addSelectedToProgram" 
            class="btn-success"
          >
            <span class="btn-icon">+</span>
            Add to Program
          </button>
          <button 
            v-if="activeTab === 'current' && selectedBeneficiaries.length > 0"
            @click="removeSelectedFromProgram" 
            class="btn-danger"
          >
            <span class="btn-icon">−</span>
            Remove from Program
          </button>
          <button @click="exportBeneficiaries" class="btn-secondary">
            Export List
          </button>
        </div>
      </div>

      <!-- Selection Info -->
      <div v-if="selectedBeneficiaries.length > 0" class="selection-info">
        <span>{{ selectedBeneficiaries.length }} user(s) selected</span>
        <button @click="clearSelection" class="clear-selection">Clear Selection</button>
      </div>

      <!-- Beneficiaries Table -->
      <div class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th>
                <input 
                  type="checkbox" 
                  @change="toggleSelectAll"
                  :checked="isAllSelected"
                  :indeterminate="isPartiallySelected"
                  class="select-checkbox"
                />
              </th>
              <th>User</th>
              <th>Account Type</th>
              <th v-if="activeTab === 'current'">Status</th>
              <th>Program</th>
              <th>Performance</th>
              <th>Group</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="beneficiary in filteredBeneficiaries" :key="beneficiary.userId || beneficiary.id">
              <td>
                <input 
                  type="checkbox" 
                  :value="activeTab === 'current' ? (beneficiary.userId || beneficiary.id) : beneficiary.id"
                  v-model="selectedBeneficiaries"
                  class="select-checkbox"
                />
              </td>
              <td>
                <div class="user-info" v-if="$route.params.type === 'coupons'">
                  <div class="user-avatar">
                    <img :src="generateAvatarUrl(beneficiary.userName)" :alt="beneficiary.userName" />
                  </div>
                  <div class="user-details">
                    <div class="user-name">{{ beneficiary.userName }}</div>
                    <div class="user-email">{{ beneficiary.userEmail }}</div>
                  </div>
                </div>
                <div class="user-info" v-else>
                  <div class="user-avatar">
                    <img :src="generateAvatarUrl(beneficiary.name || beneficiary.userName)" :alt="beneficiary.name || beneficiary.userName" />
                  </div>
                  <div class="user-details">
                    <div class="user-name">{{ beneficiary.name || beneficiary.userName }}</div>
                    <div class="user-email">{{ beneficiary.email || beneficiary.userEmail }}</div>
                  </div>
                </div>
              </td>
              <td>
                <span class="account-type">{{ beneficiary.accountType || beneficiary.userType }}</span>
              </td>
              <td v-if="activeTab === 'current'">
                <span :class="['status-badge', beneficiary.isUsed ? 'inactive' : 'active']">
                  {{ beneficiary.isUsed ? 'Claimed' : 'Unused' }}
                </span>
              </td>
              <td>
                <span class="program-name">
                  {{ beneficiary.programName || (activeTab === 'current' ? 'Enrolled' : 'Not Enrolled') }}
                </span>
              </td>
              <td>
                <span class="performance-value">{{ beneficiary.performance || '-' }}</span>
              </td>
              <td>
                <span class="group-name">{{ beneficiary.groupName || beneficiary.group || '-' }}</span>
              </td>
              <td>
                <span class="amount-value">{{ beneficiary.amount || beneficiary.amountSpent ? formatCurrency(beneficiary.amount || beneficiary.amountSpent) : '-' }}</span>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Empty State -->
        <div v-if="filteredBeneficiaries.length === 0" class="empty-state">
          <div class="empty-icon">👥</div>
          <h3>No {{ activeTab === 'current' ? 'current beneficiaries' : 'available users' }} found</h3>
          <p>{{ searchQuery ? 'Try adjusting your search criteria' : getEmptyStateMessage() }}</p>
        </div>
      </div>

      <!-- Pagination -->
      <PaginationControls
        v-if="filteredBeneficiaries.length > 0"
        :current-page="currentPage"
        :is-last-page="isLastPage"
        :pagination-info="paginationInfo"
        @previous-page="previousPage"
        @next-page="nextPage"
      />
    </div>

    <!-- Add Beneficiary Modal -->
    <AddBeneficiaryModal
      v-if="showAddBeneficiaryModal"
      :show="showAddBeneficiaryModal"
      :coupon-id="couponId"
      @close="showAddBeneficiaryModal = false"
      @beneficiaries-added="handleBeneficiariesAdded"
    />
  </div>
  </MasterLayout>
</template>

<script>
import rewardMgtService from '../../../services/rewardMgt-service';
import Loader from "@/components/UI/Loader";
import PaginationControls from './components/PaginationControls.vue';
import AddBeneficiaryModal from './components/AddBeneficiaryModal.vue';
import { useToast } from 'vue-toastification';
import MasterLayout from '../../../views/MasterLayout.vue';
import Navbar from '../Navbar.vue';

export default {
  name: 'ManageBeneficiaries',
  components: {
    Loader,
    PaginationControls,
    AddBeneficiaryModal,
    MasterLayout,
    Navbar
  },
  props: {
    couponId: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: 'coupons'

    }
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      type : 'coupons',
      isLoading: false,
      searchQuery: '',
      usageFilter: '',
      currentPage: 1,
      itemsPerPage: 10,
      actionMenuOpen: null,
      showAddBeneficiaryModal: false,
      activeTab: 'current', // 'current' or 'available'
      selectedBeneficiaries: [],
      
      // Tab configuration
      tabs: [
        { key: 'current', label: 'Current Beneficiaries' },
        { key: 'available', label: 'Available Users' }
      ],
      
      // Coupon data from API
      couponData: {},
      beneficiaries: [], // Current beneficiaries
      availableUsers: [] // Available users who can be added
    };
  },
  
  computed: {
    totalBeneficiaries() {
      return this.beneficiaries.length;
    },

    pendingRedemptions() {
      return this.beneficiaries.filter(b => !b.isUsed).length;
    },
    
    remainingUses() {
      const usageLimit = this.couponData.usageLimit || 0;
      const usedCount = this.couponData.usedCount || 0;
      return Math.max(0, usageLimit - usedCount);
    },
    
    currentDataSet() {
      return this.activeTab === 'current' ? this.beneficiaries : this.availableUsers;
    },
    
    filteredBeneficiaries() {
      let filtered = this.currentDataSet;
      
      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(beneficiary => {
          const userName = beneficiary.userName || beneficiary.name || '';
          const userEmail = beneficiary.userEmail || beneficiary.email || '';
          return userName.toLowerCase().includes(query) ||
                 userEmail.toLowerCase().includes(query);
        });
      }
      
      // Filter by usage status (only for current beneficiaries)
      if (this.activeTab === 'current') {
        if (this.usageFilter === 'used') {
          filtered = filtered.filter(beneficiary => beneficiary.isUsed);
        } else if (this.usageFilter === 'unused') {
          filtered = filtered.filter(beneficiary => !beneficiary.isUsed);
        }
      }
      
      // Pagination
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      
      return filtered.slice(start, end);
    },
    
    totalFilteredItems() {
      let filtered = this.currentDataSet;
      
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(beneficiary => {
          const userName = beneficiary.userName || beneficiary.name || '';
          const userEmail = beneficiary.userEmail || beneficiary.email || '';
          return userName.toLowerCase().includes(query) ||
                 userEmail.toLowerCase().includes(query);
        });
      }
      
      if (this.activeTab === 'current') {
        if (this.usageFilter === 'used') {
          filtered = filtered.filter(beneficiary => beneficiary.isUsed);
        } else if (this.usageFilter === 'unused') {
          filtered = filtered.filter(beneficiary => !beneficiary.isUsed);
        }
      }
      
      return filtered.length;
    },
    
    paginationInfo() {
      const start = (this.currentPage - 1) * this.itemsPerPage + 1;
      const end = Math.min(this.currentPage * this.itemsPerPage, this.totalFilteredItems);
      return `${start} - ${end} of ${this.totalFilteredItems} row(s) selected.`;
    },
    
    isLastPage() {
      return this.currentPage * this.itemsPerPage >= this.totalFilteredItems;
    },
    
    isAllSelected() {
      if (this.filteredBeneficiaries.length === 0) return false;
      
      return this.filteredBeneficiaries.every(b => {
        const id = this.activeTab === 'current' ? (b.userId || b.id) : b.id;
        return this.selectedBeneficiaries.includes(id);
      });
    },
    
    isPartiallySelected() {
      return this.selectedBeneficiaries.length > 0 && !this.isAllSelected;
    }
  },
  
  watch: {
    activeTab() {
      this.clearSelection();
      this.currentPage = 1;
    }
  },
  
  async mounted() {
    await this.loadCouponData();
    await this.loadAvailableUsers();
    
    // Handle click outside for action menu
    this.handleClickOutside = (event) => {
      if (!event.target.closest('.action-dropdown')) {
        this.actionMenuOpen = null;
      }
    };
    
    document.addEventListener('click', this.handleClickOutside);
  },
  
  beforeUnmount() {
    if (this.handleClickOutside) {
      document.removeEventListener('click', this.handleClickOutside);
    }
  },
  
  methods: {
    async loadCouponData() {
      this.isLoading = true;
      try {
        const couponId = this.$route.params.id;
        const type = this.$route.params.type;
        console.log(type, couponId);

        if (type == 'coupons'){
          const response = await rewardMgtService.getCouponById(couponId);
          if (response.data) {
          this.couponData = response.data;
          this.beneficiaries = response.data.beneficiaries || [];
        } else {
          this.toast.error('Failed to load coupon data');
        }
        } else if (type == 'cashback') {
          this.type = 'cashback';
          const response = await rewardMgtService.getCashbackById(couponId);
          if (response.data) {
            this.couponData = response.data;
            this.beneficiaries = response.data.beneficiaries || [];
          } else {
            this.toast.error('Failed to load cashback data');
          }
        } else {
          const response = await rewardMgtService.getCashbackById(couponId);
          if (response.data) {
            this.couponData = response.data;
            this.beneficiaries = response.data.beneficiaries || [];
          } else {
            this.toast.error('Failed to load coupon data');
          }
        }
        

        
      } catch (error) {
        console.error('Error loading coupon data:', error);
        this.toast.error('Failed to load coupon data. Please try again.');
      } finally {
        this.isLoading = false;
      }
    },
    
    async loadAvailableUsers() {
      try {
        // Load all customers who are not current beneficiaries
        const response = await rewardMgtService.getAllCustomers();
        if (response.data) {
          // Map the API response to match our expected format
          const allUsers = response.data.map(user => ({
            id: user.id,
            userName: user.name,
            userEmail: user.email,
            phoneNumber: user.phoneNumber,
            accountType: user.userType,
            isActive: true,
            dateRegistered: user.dateRegistered,
            programName: null,
            performance: null,
            groupName: null,
            group: null,
            amount: null,
            amountSpent: null
          }));
          
          // Filter out users who are already beneficiaries
          this.availableUsers = allUsers.filter(user => 
            !this.beneficiaries.some(b => 
              b.userId === user.id || 
              b.userEmail === user.userEmail ||
              b.id === user.id
            )
          );
        }
      } catch (error) {
        console.error('Error loading available users:', error);
        this.toast.error('Failed to load available users');
      }
    },
    
    getTabCount(tabKey) {
      return tabKey === 'current' ? this.beneficiaries.length : this.availableUsers.length;
    },
    
    getEmptyStateMessage() {
      return this.activeTab === 'current' 
        ? 'No beneficiaries have been assigned to this coupon yet.'
        : 'No available users to add to this program.';
    },
    
    // Selection methods
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedBeneficiaries = [];
      } else { 
        this.selectedBeneficiaries = this.filteredBeneficiaries.map(b => {
          return this.activeTab === 'current' ? (b.userId || b.id) : b.id;
        });
      }
    },
    
    clearSelection() {
      this.selectedBeneficiaries = [];
    },
    
    // Bulk actions
    async addSelectedToProgram() {
      if (this.selectedBeneficiaries.length === 0) return;
      
      try {
        const usersToAdd = this.availableUsers.filter(user => 
          this.selectedBeneficiaries.includes(user.id)
        );

        const couponId = this.$route.params.id;
        const type = this.$route.params.type;
        
        let payloadData;
        
        if (type === 'cashback') {
          // For cashback, send full user data as JSON array
          payloadData = usersToAdd.map(user => ({
            userId: user.id,
            email: user.userEmail,
            name: user.userName,
            userType: user.accountType || 'Business'
          }));
          
          await rewardMgtService.applyCashbackToCustomers(couponId, payloadData);
        } else {
          // For coupons, send only user IDs
          payloadData = this.selectedBeneficiaries;
          await rewardMgtService.applyCouponToCustomers(couponId, payloadData);
        }
        
        // Move users from available to current
        this.beneficiaries.push(...usersToAdd);
        this.availableUsers = this.availableUsers.filter(user => 
          !this.selectedBeneficiaries.includes(user.id)
        );
        
        this.toast.success(`${this.selectedBeneficiaries.length} user(s) added to program`);
        this.clearSelection();
      } catch (error) {
        console.error('Error adding users to program:', error);
        this.toast.error('Failed to add users to program');
      }
    },
    
    async removeSelectedFromProgram() {
      if (this.selectedBeneficiaries.length === 0) return;

      console.log('Removing beneficiaries:', this.selectedBeneficiaries);
      
      if (confirm(`Are you sure you want to remove ${this.selectedBeneficiaries.length} user(s) from this program?`)) {
        try {
          const usersToRemove = this.beneficiaries.filter(user => 
            this.selectedBeneficiaries.includes(user.userId || user.id)
          );

          const couponId = this.$route.params.id;

          const type = this.$route.params.type;

          if (type == 'coupons') {
            await rewardMgtService.removeBeneficiariesFromCoupon(couponId, this.selectedBeneficiaries);
          } else if (type == 'cashback') {
            await rewardMgtService.removeBeneficiariesFromCashback(couponId, this.selectedBeneficiaries);
          } else {
            await rewardMgtService.removeBeneficiariesFromCoupon(couponId, this.selectedBeneficiaries);
          }

          // Transform beneficiaries back to available user format
          const transformedUsers = usersToRemove.map(beneficiary => ({
            id: beneficiary.id || beneficiary.userId,
            userName: beneficiary.userName,
            userEmail: beneficiary.userEmail,
            phoneNumber: beneficiary.phoneNumber,
            accountType: beneficiary.accountType || 'Business',
            isActive: true,
            dateRegistered: beneficiary.dateRegistered,
            programName: null,
            performance: null,
            groupName: null,
            group: null,
            amount: null,
            amountSpent: null
          }));
          
          // Move users from current to available
          this.availableUsers.push(...transformedUsers);
          this.beneficiaries = this.beneficiaries.filter(user => 
            !this.selectedBeneficiaries.includes(user.userId || user.id)
          );
          
          this.toast.success(`${this.selectedBeneficiaries.length} user(s) removed from program`);
          this.clearSelection();
        } catch (error) {
          console.error('Error removing users from program:', error);
          this.toast.error('Failed to remove users from program');
        }
      }
    },
    
    // Navigation
    goBack() {
      this.$router.go(-1);
    },
    
    // Formatting methods
    formatNumber(num) {
      if (!num && num !== 0) return '0';
      return num.toLocaleString();
    },
    
    formatCurrency(amount) {
      if (!amount && amount !== 0) return '₦0';
      return `₦${amount.toLocaleString()}`;
    },
    
    formatValue() {
      const value = this.couponData.value || 0;
      const isPercentage = this.couponData.couponType === 1;
      return isPercentage ? `${value}%` : this.formatCurrency(value);
    },
    
    formatDate(dateString) {
      if (!dateString) return '-';
      try {
        return new Date(dateString).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return 'Invalid Date';
      }
    },
    
    formatPeriod() {
      const startDate = this.couponData.startDate;
      const endDate = this.couponData.endDate;
      
      if (!startDate || !endDate) return 'N/A';
      
      try {
        const start = new Date(startDate).toLocaleDateString('en-US', {
          month: 'short',
          day: '2-digit',
          year: 'numeric'
        });
        const end = new Date(endDate).toLocaleDateString('en-US', {
          month: 'short',
          day: '2-digit', 
          year: 'numeric'
        });
        return `${start} - ${end}`;
      } catch (error) {
        return 'Invalid Date Range';
      }
    },
    
    getStatusClass() {
      if (this.couponData.isExpired) return 'expired';
      if (this.couponData.isActive) return 'active';
      return 'inactive';
    },
    
    getStatusText() {
      if (this.couponData.isExpired) return 'Expired';
      if (this.couponData.isActive) return 'Active';
      return 'Inactive';
    },
    
    generateAvatarUrl(name) {
      if (!name) return 'https://ui-avatars.com/api/?name=User&background=6b7280&color=fff';
      const colors = ['6366f1', '277f34', '8b5cf6', 'f59e0b', 'ef4444', '06b6d4'];
      const colorIndex = name.length % colors.length;
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=${colors[colorIndex]}&color=fff`;
    },
    
    // Action methods
    toggleActionMenu(beneficiaryId) {
      this.actionMenuOpen = this.actionMenuOpen === beneficiaryId ? null : beneficiaryId;
    },
    
    viewBeneficiaryDetails(beneficiary) {
      console.log('Viewing beneficiary details:', beneficiary);
      this.actionMenuOpen = null;
    },
    
    async sendReminder(beneficiary) {
      if (beneficiary.isUsed) {
        this.toast.warning('Cannot send reminder to users who have already used the coupon');
        return;
      }
      
      try {
        await rewardMgtService.sendCouponReminder(beneficiary.userId, this.couponId);
        this.toast.success(`Reminder sent to ${beneficiary.userName}`);
      } catch (error) {
        console.error('Error sending reminder:', error);
        this.toast.error('Failed to send reminder');
      }
      
      this.actionMenuOpen = null;
    },
    
    async addBeneficiary(beneficiary) {
      try {
        await rewardMgtService.addBeneficiaryToCoupon(this.couponId, beneficiary.id);
        
        // Transform available user to beneficiary format
        const newBeneficiary = {
          ...beneficiary,
          assignedDate: new Date().toISOString(),
          isUsed: false,
          usedDate: null,
          userId: beneficiary.id // Ensure we have both id and userId
        };
        
        // Move from available to current
        this.beneficiaries.push(newBeneficiary);
        this.availableUsers = this.availableUsers.filter(u => u.id !== beneficiary.id);
        
        this.toast.success(`${beneficiary.userName} added to program`);
      } catch (error) {
        console.error('Error adding beneficiary:', error);
        this.toast.error('Failed to add beneficiary');
      }
      
      this.actionMenuOpen = null;
    },
    
    async removeBeneficiary(beneficiary) {
      if (confirm(`Are you sure you want to remove "${beneficiary.userName}" from this coupon?`)) {
        try {
          await rewardMgtService.removeBeneficiaryFromCoupon(beneficiary.id);
          
          // Transform beneficiary back to available user format
          const transformedUser = {
            id: beneficiary.id || beneficiary.userId,
            userName: beneficiary.userName,
            userEmail: beneficiary.userEmail,
            phoneNumber: beneficiary.phoneNumber,
            accountType: beneficiary.accountType || 'Business',
            isActive: true,
            dateRegistered: beneficiary.dateRegistered,
            programName: null,
            performance: null,
            groupName: null,
            group: null,
            amount: null,
            amountSpent: null
          };
          
          // Move from current to available
          this.availableUsers.push(transformedUser);
          this.beneficiaries = this.beneficiaries.filter(b => b.id !== beneficiary.id);
          
          this.toast.success(`${beneficiary.userName} removed successfully`);
        } catch (error) {
          console.error('Error removing beneficiary:', error);
          this.toast.error('Failed to remove beneficiary');
        }
      }
      
      this.actionMenuOpen = null;
    },
    
    handleBeneficiariesAdded(newBeneficiaries) {
      this.beneficiaries.push(...newBeneficiaries);
      this.toast.success(`${newBeneficiaries.length} beneficiaries added successfully`);
    },
    
    exportBeneficiaries() {
      const dataToExport = this.activeTab === 'current' ? this.beneficiaries : this.availableUsers;
      
      const csvData = dataToExport.map(b => ({
        Name: b.userName || b.name,
        Email: b.userEmail || b.email,
        'Account Type': b.accountType || b.userType || 'Business',
        Status: b.isActive ? 'Active' : 'Inactive',
        Program: b.programName || (this.activeTab === 'current' ? 'Enrolled' : 'Not Enrolled'),
        Performance: b.performance || '',
        Group: b.groupName || b.group || '',
        Amount: b.amount || b.amountSpent || ''
      }));
      
      const csv = this.convertToCSV(csvData);
      this.downloadCSV(csv, `coupon-${this.couponData.couponCode}-${this.activeTab}-users.csv`);
    },
    
    convertToCSV(data) {
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(header => `"${row[header]}"`).join(','))
      ].join('\n');
      
      return csvContent;
    },
    
    downloadCSV(content, filename) {
      const blob = new Blob([content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    },
    
    // Pagination methods
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    
    nextPage() {
      if (!this.isLastPage) {
        this.currentPage++;
      }
    }
  }
};
</script>

<style scoped>
.manage-beneficiaries {
  margin-top: 40px;
  padding: 24px;
  background: #f8f9fa;
  min-height: 100vh;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  font-size: 18px;
  color: #6b7280;
}

/* Header Section */
.page-header {
  margin-bottom: 24px;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  background: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.2s ease;
}

.back-btn:hover {
  color: #374151;
}

.back-icon {
  font-size: 18px;
}

/* Program Details */
.program-details {
  margin-bottom: 32px;
}

.program-details h1 {
  margin: 0 0 16px 0;
  font-size: 28px;
  font-weight: 600;
  color: #111827;
}

.program-meta {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.meta-item {
  font-size: 14px;
  color: #6b7280;
}

.meta-item strong {
  color: #374151;
}

/* Stats Section */
.stats-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 32px;
}

.stat-card {
  background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid #f3f4f6;
  transition: transform 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.stat-header h3 {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stat-number {
  margin: 0 0 8px 0;
  font-size: 32px;
  font-weight: 700;
  color: #111827;
}

/* Tab Controls */
.tab-container {
  margin-bottom: 24px;
}

.tab-header {
  display: flex;
  width: fit-content;
  background: #f3f4f6;
  border-radius: 20px;
  padding: 4px;
  gap: 1px;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  background: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  border-bottom: 3px solid transparent;
  transition: all 0.2s ease;
}

.tab-btn.active {
  background: #ffffff;
  color: #000000;
  border-radius: 20px;
}

.tab-count {
  background: #e5e7eb;
  color: #6b7280;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  min-width: 20px;
  text-align: center;
}

.tab-btn.active .tab-count {
  background: #dbeafe;
  color: #1d4ed8;
}

/* Controls Section */
.controls-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 16px;
}

.search-filters {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.search-input {
  padding: 10px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  width: 300px;
  outline: none;
  font-size: 14px;
  transition: border-color 0.2s ease;
}

.search-input:focus {
  border-color: #3b82f6;
}

.filter-select {
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  outline: none;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.filter-select:focus {
  border-color: #3b82f6;
}

.actions-section {
  display: flex;
  gap: 12px;
}

.btn-primary,
.btn-secondary,
.btn-success,
.btn-danger {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-success {
  background: #277f34;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #059669;
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
}

.btn-secondary {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover {
  background: #f9fafb;
}

.btn-icon {
  font-size: 16px;
  font-weight: bold;
}

/* Selection Info */
.selection-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
  color: #1d4ed8;
}

.clear-selection {
  background: none;
  border: none;
  color: #3b82f6;
  cursor: pointer;
  font-size: 14px;
  text-decoration: underline;
}

.clear-selection:hover {
  color: #1d4ed8;
}

/* Table Section */
.table-container {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid #f3f4f6;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  background: #f9fafb;
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.data-table td {
  padding: 16px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 14px;
  vertical-align: middle;
}

.data-table tr:hover {
  background-color: #f9fafb;
}

/* Checkbox */
.select-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

/* User Info Styles */
.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-details {
  min-width: 0;
}

.user-name {
  font-weight: 600;
  color: #111827;
  margin-bottom: 2px;
}

.user-email {
  color: #6b7280;
  font-size: 12px;
}

/* Status Badge */
.status-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.status-badge.active {
  background: #dcfce7;
  color: #166534;
}

.status-badge.inactive {
  background: #f3f4f6;
  color: #6b7280;
}

.status-badge.expired {
  background: #fee2e2;
  color: #dc2626;
}

.status-badge.used {
  background: #dcfce7;
  color: #166534;
}

.status-badge.unused {
  background: #fef3c7;
  color: #92400e;
}

/* Other table content styles */
.account-type,
.program-name,
.performance-value,
.group-name,
.amount-value {
  color: #374151;
  font-size: 14px;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 64px 24px;
  color: #6b7280;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-state h3 {
  margin: 0 0 8px 0;
  color: #374151;
  font-size: 18px;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .stats-container {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .manage-beneficiaries {
    padding: 16px;
  }
  
  .program-meta {
    flex-direction: column;
    gap: 8px;
  }
  
  .stats-container {
    grid-template-columns: 1fr;
  }
  
  .tab-header {
    flex-direction: column;
  }
  
  .controls-section {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-filters {
    flex-direction: column;
  }
  
  .search-input {
    width: 100%;
  }
  
  .actions-section {
    justify-content: stretch;
  }
  
  .btn-primary,
  .btn-secondary,
  .btn-success,
  .btn-danger {
    flex: 1;
    justify-content: center;
  }
  
  .data-table {
    font-size: 12px;
  }
  
  .data-table th,
  .data-table td {
    padding: 12px 8px;
  }
}

@media (max-width: 480px) {
  .user-avatar {
    width: 32px;
    height: 32px;
  }
  
  .user-name {
    font-size: 12px;
  }
  
  .user-email {
    font-size: 10px;
  }
  
  .stat-number {
    font-size: 24px;
  }
  
  .tab-btn {
    padding: 10px 16px;
    font-size: 13px;
  }
}
</style>